import { useState } from 'react';
import { Heart, Share2 } from 'lucide-react';
import { useAppStore } from '@/lib/store';
import { useAppLanguage } from '@/hooks/useAppLanguage';
import { getTranslation } from '@/constants/translations';
import { Toast } from './Toast';

interface DetailActionButtonsProps {
  id: string;
  title: string;
  shareUrl: string;
  variant?: 'dark' | 'light';
  hideShare?: boolean;
}

export function DetailActionButtons({ id, title, shareUrl, variant = 'dark', hideShare = false }: DetailActionButtonsProps) {
  const { language } = useAppLanguage();
  const { isFavorite, toggleFavorite } = useAppStore();

  const isLight = variant === 'light';
  const iconColor = isLight ? '#4A7C9B' : 'white';
  const buttonBg = isLight ? '#E8F4F8' : 'rgba(0,0,0,0.4)';
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const favorited = isFavorite(id);

  const handleFavoriteClick = () => {
    toggleFavorite(id);
    const message = favorited
      ? getTranslation('removedFromFavorites', language)
      : getTranslation('addedToFavorites', language);
    setToastMessage(message);
    setShowToast(true);

    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
  };

  const handleShareClick = async () => {
    const fullUrl = `${window.location.origin}${shareUrl}`;

    // Try native Web Share API first
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          url: fullUrl,
        });
        return;
      } catch (err) {
        // User cancelled or error - fall back to clipboard
        if ((err as Error).name === 'AbortError') {
          return; // User cancelled, don't show anything
        }
      }
    }

    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(fullUrl);
      setToastMessage(getTranslation('linkCopied', language));
      setShowToast(true);

      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate(10);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <>
      <div style={styles.container}>
        <button
          onClick={handleFavoriteClick}
          style={{ ...styles.button, background: buttonBg }}
          aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
        >
          <Heart
            size={24}
            color={favorited ? '#D35D5D' : iconColor}
            fill={favorited ? '#D35D5D' : 'transparent'}
          />
        </button>

        {!hideShare && (
          <button
            onClick={handleShareClick}
            style={{ ...styles.button, background: buttonBg }}
            aria-label="Share"
          >
            <Share2 size={24} color={iconColor} />
          </button>
        )}
      </div>

      <Toast
        message={toastMessage}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </>
  );
}

const styles = {
  container: {
    display: 'flex',
    gap: '8px',
  } as React.CSSProperties,

  button: {
    background: 'rgba(0,0,0,0.4)',
    border: 'none',
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  } as React.CSSProperties,
};
